import type { SystemRolePermissionId } from '@clockwise/shared';
import type { ReactNode } from 'react';
import { NoAccess } from '@/components/custom/no-access';
import { useMe } from '@/lib/me';

/**
 * Route-level permission gate. Returns whether the caller holds `permission`,
 * plus a `fallback` node to render INSTEAD of the page when they don't.
 *
 * Usage (after every other hook, immediately before the page JSX):
 *
 *   const { allowed, fallback } = usePermissionGuard('activity.edit', 'activities');
 *   const detail = useQuery({ ..., enabled: allowed });
 *   if (fallback) return fallback;
 *   return <PageShell>…</PageShell>;
 *
 * While `useMe()` is still resolving we return `allowed: false` with a `null`
 * fallback (NOT the NoAccess panel): the cached `/users/me` can momentarily be
 * absent (e.g. after `queryClient.clear()` on sign-out, or a background
 * refetch), leaving the permission set briefly empty. Returning NoAccess there
 * would flash "no access" at a user who actually holds the permission. Gating
 * the data query on `allowed` also defers the fetch until permissions are known.
 */
export function usePermissionGuard(
  permission: SystemRolePermissionId,
  resource?: string,
): { allowed: boolean; loading: boolean; fallback: ReactNode } {
  const { permissions, loading } = useMe();
  const allowed = permissions.has(permission);
  const fallback: ReactNode =
    !loading && !allowed ? <NoAccess permission={permission} resource={resource} /> : null;
  return { allowed, loading, fallback };
}
